import React, { useLayoutEffect, useRef, useState } from 'react';
import { TypedMemo } from '../../common/types';
import { PreviewDevice } from './toolbar/previewDevice';
import styles from './SMLPreviewStage.module.scss';

/** The viewport the desktop preview is rendered at before being scaled to fit.
    A real window size, so the page centres its content the way a desktop
    visitor sees it — deriving the height from the column instead made the
    frame ~1800px tall and pushed everything to the middle. */
const DESKTOP_WIDTH = 1280;
const DESKTOP_HEIGHT = 800;

interface SMLPreviewStageProps {
  device: PreviewDevice;
}

const SMLPreviewStageComponent: React.FC<React.PropsWithChildren<SMLPreviewStageProps>> = ({ device, children }) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(0);

  useLayoutEffect(() => {
    const element = viewportRef.current;
    if (element === null || device !== 'desktop') {
      return undefined;
    }
    const observer = new ResizeObserver(([entry]) => {
      if (entry === undefined) {
        return;
      }
      setWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [device]);

  if (device === 'mobile') {
    return (
      <div className={styles.stage}>
        <div className={styles.mobile}>{children}</div>
      </div>
    );
  }

  const scale = width > 0 ? width / DESKTOP_WIDTH : 1;

  return (
    <div className={styles.stage}>
      <div ref={viewportRef} className={styles.desktopViewport} style={{ height: DESKTOP_HEIGHT * scale }}>
        <div
          className={styles.desktopFrame}
          style={{ width: DESKTOP_WIDTH, height: DESKTOP_HEIGHT, transform: `scale(${scale})` }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export const SMLPreviewStage = TypedMemo(SMLPreviewStageComponent);
